import React, {useContext} from 'react'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import {faFileExport, faSpinner} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

import AddStandard from './add-standard'

import {truncateText, isEmpty} from 'helpers/utils'
import {createPost} from 'helpers/command'
import {getQuizQuestionStandards} from 'helpers/quiz'

import {isValidQuestion} from 'helpers/quiz'
import {TeacherContext} from 'teacher/context'

import QuestionValue from 'components/quiz/components/question-value'
import useStyles from './styles'
import questionHandlers, {propertyHandlers} from 'components/quiz/edit-questions'
import QuestionValidations from './QuestionValidations'

function findQuestionIndex (id, questions = {}) {
  for (let section in questions) {
    const index = questions[section].findIndex(q => q.id == id)
    if (index >= 0) return {index, section}
  }
  return {index: undefined, section: undefined}
}

export default function AssessmentQuestion () {

  const classes = useStyles()
  const context = useContext(TeacherContext)
  const {at, questionId, quizQuestions, translate} = context

  const {index, section} = findQuestionIndex(questionId, quizQuestions)

  const questionPath = `quizQuestions[${section}][${index}]`
  const [question = {}] = at(questionPath)

  const questionType = context.quizQuestionTypes.find(type => type.id == question.type) || {}

  const Handler = questionHandlers[questionType.name]
  const PropertyHandler = propertyHandlers[questionType.name]

  const truncate = truncateText(69)

  const [saving, setSaving] = React.useState(false)

  async function saveToBank (replace) {
    setSaving(true)
    const [updatedId, template] = replace
      ? await createPost('quiz', 'updateBankQuestion')({question})
      : await createPost('quiz', 'saveQuestionToBank')({id: question.id})
    setSaving(false)
    if (updatedId == question.id) {
      context.updateState({
        quizQuestions: {[question.section]: {[index]: {template: {$set: template.id}}}},
        quizQuestionTemplate: {$set: template}
      })
    }
  }

  function handleSaveToBank() {
    if (!saving) saveToBank()
  }

  async function updateProperties (request, updateCommand) {
    if (updateCommand) {
      await context.updateState({quizQuestions: {[question.section]: {[index]: {
        ...updateCommand, modifications: {$apply: i => i + 1}
      }}}})
    }
    if (request) {
      createPost('quiz', 'updateQuestionProperties')(request)
    }
  }

  const handlePropertyChange = (name, value) => updateProperties({id: question.id, [name]: value}, {[name]: {$set: value}})

  const onStandardsChange = async standards => {
    const newContext = await context.updateState({quizQuestions: {[question.section]: {[index]: {properties: {standards: {$set: standards}}}}}})
    await context.updateState({quizQuestionStandards: {$set: getQuizQuestionStandards(newContext)}}) 
    createPost('quiz', 'updateQuestionProperties')({id: question.id, properties: {...question.properties, standards}})
  }

  const questionTypeName = context.translate(questionType.name) || ''
  const questionTitle = isEmpty(question.textpremise)
    ? questionTypeName
    : `${questionTypeName}: ${truncate(question.textpremise)}`

  const canSaveToBank = isValidQuestion(question, questionType.name)

  return (
      <Paper key={question.id} className={classes.root}>
        <Box className={classes.header}>
          <Box className={classes.questionName}>
            <Box className="indexLabel">{question.index + 1}.</Box>
            <Box className="title">
              {questionTitle}
            </Box>
          </Box>
          {canSaveToBank && (
          <Box className="saveToBank" onClick={handleSaveToBank}>
            {!saving && <><div className="icon"><FontAwesomeIcon icon={faFileExport} /></div><div className="label">{context.translate('save-to-bank')}</div></>}
            {saving && <div className="icon"><FontAwesomeIcon icon={faSpinner} spin /></div>}
          </Box>)}
        </Box>
        <Box className={classes.content}>
          <Box className="top-properties">
            <Box className="properties">
              {PropertyHandler && <PropertyHandler id={question.id} context={context} question={question} updateProperties={updateProperties} />}
              {!question.isNew && <QuestionValidations {...{question, type: questionType, context}} />}
            </Box>
            <Box className={classes.filler} />
            <Box className="value-wrapper">
              <Box className="value-container">
                <QuestionValue id={question.id} context={context} className="value" question={question} updateProperties={updateProperties} />
                {questionType.name == 'matching' && (<div className="value-disclaimer">{translate('matching-value-disclaimer')}</div>)}
              </Box>
            </Box>
          </Box>
          <Box className="standards">
            <AddStandard onStandardsChange={onStandardsChange} question={question} />
          </Box>
          {Handler && <Handler
            index={index}
            context={context}
            onPropertyChange={handlePropertyChange}
            updateProperties={updateProperties}
            question={question}
            questionPath={questionPath}
          />}
        </Box>
      </Paper>
  )
}
